import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { GameState, GameActions, GameHelpers, Lane, PlayerId, CardDefinition } from '../../types/kotch';
import { LANES } from '../../types/kotch';
import CardHand from './CardHand';
import OpponentHand from './OpponentHand';
import CommitZone from './CommitZone';
import LaneSlot from './LaneSlot';
import PlayerBanner from './PlayerBanner';
import MatchInfoPanel from './MatchInfoPanel';
import DeckDisplay from './DeckDisplay';
import DiscardPileDisplay from './DiscardPileDisplay';
import CardDetailPanel from './CardDetailPanel';
import { makeAICommitment, getAIThinkingDelay } from '../../utils/kotch/aiLogic';

interface BattlefieldViewProps {
  state: GameState;
  actions: GameActions;
  helpers: GameHelpers;
  myPlayerId?: PlayerId | null;  // For online multiplayer
  isOnline?: boolean;
  isHost?: boolean;              // Whether this client is the host
  onOnlineCommit?: () => void;   // Called when online player confirms (to send to peer)
}

export default function BattlefieldView({ state, actions, helpers, myPlayerId, isOnline = false, isHost = false, onOnlineCommit }: BattlefieldViewProps) {
  const [hoveredCardDef, setHoveredCardDef] = useState<CardDefinition | null>(null);

  const isAttackerTurn = state.commitmentSubPhase === 'attackerCommit';
  const currentPlayerId: PlayerId = isAttackerTurn ? state.currentAttackerId : state.currentDefenderId;
  const currentPlayer = state.players[currentPlayerId];

  // Determine viewing player:
  // - Online: use myPlayerId (the local player)
  // - Offline: if player 1 is AI, view as player 2; otherwise view as player 1
  const viewingPlayerId: PlayerId = isOnline && myPlayerId ? myPlayerId : (state.players[1].isAI ? 2 : 1);
  const viewingPlayer = state.players[viewingPlayerId];
  const opponentId: PlayerId = viewingPlayerId === 1 ? 2 : 1;
  const opponent = state.players[opponentId];

  const playerCommitment = helpers.getPlayerCommitment(viewingPlayerId);
  const opponentCommitment = helpers.getPlayerCommitment(opponentId);

  const canConfirm = helpers.canConfirmCommitment(viewingPlayerId);
  // In online mode, show "opponent thinking" instead of AI overlay
  const isAITurn = !isOnline && currentPlayer?.isAI && state.phase === 'commitment';
  const isOpponentTurn = isOnline && currentPlayerId !== viewingPlayerId && state.phase === 'commitment';

  // Track if we've handled resolution completion
  const resolutionHandled = useRef(false);

  // Handle AI turns
  useEffect(() => {
    if (state.phase !== 'commitment') return;
    if (!currentPlayer?.isAI) return;

    const delay = getAIThinkingDelay(currentPlayer.aiPersonality || 'medium');

    const timer = setTimeout(() => {
      const commitment = makeAICommitment(state, currentPlayerId);
      actions.aiMakeCommitment(commitment);

      // Auto-confirm after a short delay
      setTimeout(() => {
        actions.confirmCommitment();
      }, 500);
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.commitmentSubPhase, currentPlayer?.isAI]);

  // Handle reveal phase
  useEffect(() => {
    if (state.phase === 'reveal' && !state.revealed) {
      const timer = setTimeout(() => {
        actions.revealCards();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.revealed]);

  // Reset resolution handler when entering resolution phase
  useEffect(() => {
    if (state.phase === 'resolution') {
      resolutionHandled.current = false;
    }
  }, [state.phase]);

  // Handle resolution animation - simplified and more reliable
  useEffect(() => {
    if (state.phase !== 'resolution' || !state.revealed) return;

    const timer = setTimeout(() => {
      if (state.resolvingLaneIndex < 2) {
        actions.resolveNextLane();
      } else if (!resolutionHandled.current) {
        resolutionHandled.current = true;
        actions.completeRoundResolution();
      }
    }, 800);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.revealed, state.resolvingLaneIndex]);

  const handleLaneClick = (lane: Lane) => {
    if (isAITurn || isOpponentTurn) return;
    if (viewingPlayerId !== currentPlayerId) return; // Not our turn

    if (playerCommitment[lane]) {
      // Uncommit the card
      actions.uncommitCardFromLane(lane);
    } else if (state.selectedCardInstanceId) {
      // Commit selected card
      actions.commitCardToLane(lane);
    }
  };

  const handleConfirm = () => {
    if (isOnline && onOnlineCommit) {
      // In online mode, notify parent to send commitment to peer
      onOnlineCommit();
    }

    // In online mode, only host advances state locally
    // Guest waits for state update from host
    if (!isOnline || isHost) {
      actions.confirmCommitment();
    }
  };

  const isMyTurn = state.phase === 'commitment' && viewingPlayerId === currentPlayerId;

  const handleCardHover = (def: CardDefinition | null) => {
    setHoveredCardDef(def);
  };

  const isViewingAttacker = viewingPlayerId === state.currentAttackerId;
  const isOpponentAttacker = opponentId === state.currentAttackerId;

  return (
    <motion.div
      className="kotch-battlefield-layout"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Opponent Turn Overlay (AI or online opponent) */}
      <AnimatePresence>
        {(isAITurn || isOpponentTurn) && (
          <motion.div
            className="kotch-ai-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="kotch-ai-overlay-content">
              <div className="kotch-ai-thinking-icon">{isOnline ? '⚔' : '⚙'}</div>
              <h3>{opponent?.name} is {isOnline ? 'playing' : 'thinking'}...</h3>
              <p>{isAttackerTurn ? 'Planning attack' : 'Planning defense'}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Left Sidebar — Match Info */}
      <div className="kotch-match-sidebar">
        <MatchInfoPanel
          player1Name={viewingPlayer.name}
          player2Name={opponent.name}
          player1BattleWins={viewingPlayerId === 1 ? state.match.player1BattleWins : state.match.player2BattleWins}
          player2BattleWins={viewingPlayerId === 1 ? state.match.player2BattleWins : state.match.player1BattleWins}
          player1RoundWins={viewingPlayerId === 1 ? state.match.currentBattle.player1RoundWins : state.match.currentBattle.player2RoundWins}
          player2RoundWins={viewingPlayerId === 1 ? state.match.currentBattle.player2RoundWins : state.match.currentBattle.player1RoundWins}
          currentBattle={state.match.currentBattle.battleNumber}
          currentRound={state.match.currentBattle.roundNumber}
        />
      </div>

      {/* Main Battlefield */}
      <div className="kotch-battlefield">
        {/* Opponent Banner */}
        <PlayerBanner
          playerName={opponent.name}
          faction={opponent.faction}
          isAttacker={isOpponentAttacker}
          isOpponent={true}
        />

        {/* Opponent Hand */}
        <OpponentHand
          cards={opponent.hand}
          playerName={opponent.name}
          revealedCards={opponent.revealedCards}
          playerFaction={opponent.faction}
          onCardHover={handleCardHover}
        />

        {/* Opponent Commit Row: Discard | Commit Slots | Deck */}
        <div className="kotch-commit-row">
          <DiscardPileDisplay
            discardPile={opponent.discardPile}
            faction={opponent.faction}
            isOpponent={true}
          />
          <CommitZone
            commitment={opponentCommitment}
            isOpponent={true}
            playerFaction={opponent.faction}
            onCardHover={handleCardHover}
          />
          <DeckDisplay
            deck={opponent.matchDeck}
            faction={opponent.faction}
            isOpponent={true}
          />
        </div>

        {/* Lanes */}
        <div className="kotch-lanes">
          {LANES.map((lane) => {
            const laneResult = state.currentRoundResult?.laneResults.find(r => r.lane === lane);
            const myCard = viewingPlayerId === 1 ? state.player1Commitment[lane] : state.player2Commitment[lane];
            const theirCard = viewingPlayerId === 1 ? state.player2Commitment[lane] : state.player1Commitment[lane];
            return (
              <LaneSlot
                key={lane}
                lane={lane}
                player1Card={myCard}
                player2Card={theirCard}
                revealed={state.revealed}
                result={state.phase === 'resolution' || state.phase === 'roundEnd' ? laneResult : undefined}
                viewingAsPlayer={viewingPlayerId}
                player1Faction={viewingPlayer.faction}
                player2Faction={opponent.faction}
                onCardHover={handleCardHover}
              />
            );
          })}
        </div>

        {/* Player Commit Row: Discard | Commit Slots | Deck */}
        <div className="kotch-commit-row">
          <DiscardPileDisplay
            discardPile={viewingPlayer.discardPile}
            faction={viewingPlayer.faction}
            isOpponent={false}
          />
          <CommitZone
            commitment={playerCommitment}
            isOpponent={false}
            onSlotClick={handleLaneClick}
            canInteract={isMyTurn}
            selectedCardId={state.selectedCardInstanceId}
            playerFaction={viewingPlayer.faction}
            onCardHover={handleCardHover}
          />
          <DeckDisplay
            deck={viewingPlayer.matchDeck}
            faction={viewingPlayer.faction}
            isOpponent={false}
          />
        </div>

        {/* Player Hand */}
        <CardHand
          cards={viewingPlayer.hand}
          selectedCardId={state.selectedCardInstanceId}
          onCardSelect={actions.selectCard}
          disabled={!isMyTurn}
          playerFaction={viewingPlayer.faction}
          revealedCards={viewingPlayer.revealedCards}
          onCardHover={handleCardHover}
        />

        {/* Player Banner */}
        <PlayerBanner
          playerName={viewingPlayer.name}
          faction={viewingPlayer.faction}
          isAttacker={isViewingAttacker}
        />
      </div>

      {/* Right Sidebar — Card Detail + Confirm */}
      <div className="kotch-detail-sidebar">
        <CardDetailPanel
          card={hoveredCardDef}
          playerFaction={viewingPlayer.faction}
        />
        {isMyTurn && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginTop: '0.75rem' }}
          >
            <button
              className="kotch-button kotch-confirm-btn"
              onClick={handleConfirm}
              disabled={!canConfirm}
            >
              Confirm {isViewingAttacker ? 'Attack' : 'Defense'}
            </button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
